import {
  addMonths,
  differenceInSeconds,
  getMonth,
  getYear,
  isAfter,
  isEqual,
} from 'date-fns';

import {
  AsvConfigResponse,
  AsvReport,
  AsvReportFile,
  AsvScanRun,
  AsvTarget,
  CreateAsvTargetRequest,
} from '@components/features/AsvScans/types';
import {
  getHostType,
  parseIpRange,
} from '@components/features/AsvScans/validate';

export function isAsvEnrolled(config: AsvConfigResponse | null | undefined) {
  return config != null && config.asvConfig.allocatedIps > 0;
}

export type MappedAsvScanStatus =
  | 'Scheduled'
  | 'Queued'
  | 'Running'
  | 'Done'
  | 'Error'
  | 'Canceled';

export function getScanStatus(scanRun: AsvScanRun): MappedAsvScanStatus {
  const status = scanRun.status;
  switch (status) {
    case 'New':
    case 'Queued':
      return 'Queued';
    case 'Requested':
    case 'Running':
      return 'Running';
    case 'Done':
      return 'Done';
    case 'Internal Error':
      return 'Error';
    case 'Delete Requested':
    case 'Delete Ultimate Requested':
    case 'Stop Requested':
    case 'Stop Requested Giveup':
    case 'Delete Waiting':
    case 'Delete Ultimate Waiting':
    case 'Stopped':
    case 'Stop Waiting':
      return 'Canceled';
    default:
      return 'Scheduled';
  }
}

export function getScanCompliance(scanRun: AsvScanRun) {
  if (scanRun.vulnerabilities) {
    return {
      pass: isPciCompliant(scanRun.vulnerabilities.maxSeverity),
    };
  }

  return null;
}

export function isScanInProgress(scanRun: AsvScanRun) {
  const status = getScanStatus(scanRun);
  return status === 'Queued' || status === 'Running';
}

export function isScanRunning(scanRun: AsvScanRun) {
  const status = getScanStatus(scanRun);
  return status === 'Running';
}

export function isScanCompleted(scanRun: AsvScanRun) {
  const status = getScanStatus(scanRun);
  return status === 'Done';
}

export function isFileProcessing(file: AsvReportFile) {
  return file?.status === 'processing';
}

export function isReportProcessing(report: AsvReport) {
  const isExecutiveProcessing =
    !!report.files.executive && isFileProcessing(report.files.executive);

  const isJsonProcessing =
    !!report.files.jsonFile && isFileProcessing(report.files.jsonFile);

  return isExecutiveProcessing || isJsonProcessing;
}

export function getCVEs(cve: string) {
  if (cve === 'NOCVE') {
    return [];
  }

  return cve.split(/,\s*/);
}

export function getCVELink(cve: string) {
  return `https://cve.org/CVERecord?id=${cve}`;
}

export function getXrefLinks(xref: string) {
  if (xref === 'NOXREF') {
    return [];
  }

  return xref.split(/,\s*/).flatMap((link) => {
    if (link.startsWith('URL:')) {
      return [link.slice(4)];
    } else {
      return [];
    }
  });
}

// PCI DSS requires CVSS >= 4.0 be remediated
export function isPciCompliant(severity: number | string) {
  const level =
    typeof severity === 'number' ? severity : Number.parseFloat(severity);

  return level < 4;
}

export function getScanDuration(
  scanRun: AsvScanRun,
  now: string | number | Date = Date.now()
) {
  const start = scanRun.report?.timestamp ?? scanRun.createdAt;

  const hasStarted = isAfter(now, start) || isEqual(now, start);
  if (!hasStarted) {
    return {
      s: 0,
      m: 0,
      h: 0,
    };
  }

  const end = scanRun.report?.scanEnd ?? now;

  const diff = differenceInSeconds(end, start);
  const seconds = diff % 60;
  const minutes = Math.floor(diff / 60);

  return {
    s: seconds,
    m: minutes % 60,
    h: Math.floor(minutes / 60),
  };
}

export function getHostIpCount(host: string): number {
  const type = getHostType(host);
  switch (type) {
    case 'ip_range': {
      const range = parseIpRange(host);
      return range.count;
    }
    case 'ip':
    case 'hostname': {
      return 1;
    }
    default: {
      return 0;
    }
  }
}

export function getReservedIpCount(
  targets: AsvTarget[],
  additionalHosts?: string[]
): number {
  const set = new Set(targets.flatMap((target) => target.hosts));
  additionalHosts?.forEach((host) => set.add(host));

  let count = 0;
  Array.from(set).forEach((host) => {
    count += getHostIpCount(host);
  });
  return count;
}

export function getScheduleConfig(
  schedule: 'monthly' | 'quarterly'
): CreateAsvTargetRequest['schedule'] {
  if (schedule === 'monthly') {
    return {
      period: 1,
      periodUnit: 'month',
      firstTime: getNextScheduledTime('monthly'),
      byMonthDay: '1',
    };
  } else {
    return {
      period: 3,
      periodUnit: 'month',
      firstTime: getNextScheduledTime('quarterly'),
      byMonthDay: '1',
    };
  }
}

export function getNextScheduledTime(schedule: 'monthly' | 'quarterly') {
  const now = new Date();
  const year = getYear(now);

  let nextTime: Date;
  if (schedule === 'monthly') {
    // Monthly: First day of the next month
    const nextMonth = getMonth(addMonths(now, 1));
    const nextMontStr = String(nextMonth + 1).padStart(2, '0');
    const nextYear = nextMonth === 0 ? year + 1 : year;
    nextTime = new Date(`${nextYear}-${nextMontStr}-01T08:00:00.000Z`);
  } else {
    // Quarterly: Jan 1, Apr 1, Jul 1, Oct 1
    const nextYear = year + 1;
    const quarters = [
      new Date(`${year}-01-01T08:00:00.000Z`),
      new Date(`${year}-04-01T08:00:00.000Z`),
      new Date(`${year}-07-01T08:00:00.000Z`),
      new Date(`${year}-10-01T08:00:00.000Z`),
      new Date(`${nextYear}-01-01T08:00:00.000Z`),
      new Date(`${nextYear}-04-01T08:00:00.000Z`),
      new Date(`${nextYear}-07-01T08:00:00.000Z`),
      new Date(`${nextYear}-10-01T08:00:00.000Z`),
    ];

    const currentQuarter = quarters.find((quarter) => isAfter(quarter, now));
    nextTime = currentQuarter ?? quarters[0];
  }

  return nextTime.toISOString();
}
